/**
 * One uploader for championship match clips, shared by the match camera and
 * the "Videos on this phone" gallery.
 *
 * A finished minute goes straight to the server when nothing older is waiting
 * and the upload mode allows it. Otherwise it joins the back of the on-phone
 * line, so a capture's finalize piece (which stitches the tape) is never sent
 * before its earlier minutes. The line drains oldest first whenever the
 * network allows: Wi‑Fi coming back, the app returning to the foreground, or
 * a periodic retry.
 */

import {
  uploadChampionshipMedia,
  uploadChampionshipMediaAudio,
} from "../components/funtionsBD/competitionService";
import { newClientUploadId } from "../api/evaluationVideoUploadApi";
import { getNetworkTransport } from "./videoUploadNetwork";
import {
  deleteLocalMatchClip,
  isLocalClipStorageFull,
  listLocalMatchClipMeta,
  localMatchClipsMayExist,
  matchClipShouldStayOnPhone,
  matchClipUploadPlan,
  readLocalMatchClip,
  readMatchUploadMode,
  saveLocalMatchClip,
  summarizeLocalMatchClips,
  uploadModeForMatchClip,
} from "./championshipLocalClips";

/** No upload progress for this long means the connection died mid-file. */
export const CLIP_UPLOAD_STALL_MS = 45_000;
export const CLIP_AUTO_FLUSH_MS = 30_000;
/** Short in-place retries before a live minute is parked on the phone. */
export const LIVE_CLIP_RETRY_DELAYS_MS = Object.freeze([2_000, 6_000]);

const IDLE = Object.freeze({
  loaded: false,
  count: 0,
  bytes: 0,
  busy: false,
  forced: false,
  currentId: null,
  done: 0,
  total: 0,
  pct: null,
  sent: false,
  waitingReason: "",
  errors: Object.freeze({}),
  lastResult: null,
});

let state = IDLE;
const listeners = new Set();
const uploadedListeners = new Set();

function emit(patch) {
  state = { ...state, ...patch };
  listeners.forEach((fn) => {
    try {
      fn(state);
    } catch {
      /* a listener must not stop the uploader */
    }
  });
}

export function getChampionshipClipUploadState() {
  return state;
}

export function subscribeChampionshipClipUploads(fn) {
  listeners.add(fn);
  fn(state);
  return () => listeners.delete(fn);
}

/** `fn({ matchId, data })` after the server confirms a clip (snapshot inside). */
export function onChampionshipClipUploaded(fn) {
  uploadedListeners.add(fn);
  return () => uploadedListeners.delete(fn);
}

function announceUploaded(matchId, data) {
  uploadedListeners.forEach((fn) => {
    try {
      fn({ matchId: String(matchId || ""), data });
    } catch {
      /* ignore */
    }
  });
}

let recordingActive = false;

/** Phone mode waits while this phone films; the line drains once it stops. */
export function setChampionshipRecordingActive(on) {
  const next = Boolean(on);
  if (next === recordingActive) return;
  recordingActive = next;
  if (!next) kickChampionshipClipUploads();
}

/** Error shape kept per clip for the gallery. */
export function classifyClipUploadError(err) {
  if (!err) return null;
  if (isLocalClipStorageFull(err)) return { kind: "storage_full" };
  if (err.kind) return err;
  const status = Number(err?.response?.status || err?.status) || 0;
  const message = String(err?.response?.data?.message || err?.message || "");
  if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") {
    return { kind: err.bodySent ? "unconfirmed" : "stalled", status };
  }
  if (status === 413 || /entity too large|payload too large|file too large/i.test(message)) {
    return { kind: "too_large", status };
  }
  if (!status) return { kind: "network", status };
  if (status >= 500 || status === 408 || status === 429) {
    return { kind: "server", status, message };
  }
  return { kind: "rejected", status, message };
}

export function clipUploadErrorText(error, t) {
  const e = classifyClipUploadError(error);
  if (!e) return "";
  switch (e.kind) {
    case "storage_full":
      return t(
        "cmClipStorageFull",
        "This phone is out of storage. Upload or delete videos on this phone, then film again."
      );
    case "stalled":
      return t(
        "cmUploadStalled",
        "Upload stopped before it finished. Check the connection and tap Upload now again."
      );
    case "unconfirmed":
      return t(
        "cmUploadSentUnconfirmed",
        "The video was sent, but the server did not confirm it. Tap Upload now again."
      );
    case "too_large":
      return t(
        "cmVideoTooLarge",
        "Video is too large to upload. Recording will use a lower quality next time — try a shorter set, or check your connection."
      );
    case "unreadable":
      return t("cmClipUnreadable", "A saved clip could not be read on this phone.");
    case "network":
      return t("cmClipUploadNetwork", "No connection to the server. It will try again.");
    case "server":
      return t("cmClipUploadServer", "The server is busy. It will try again.");
    default:
      return e.message || t("cmVideoSaveFailed", "Could not save video — try again");
  }
}

/** One status line for the line of clips on this phone. */
export function clipUploadStatusText(s, t) {
  if (!s) return "";
  if (s.busy && s.currentId) {
    const done = Math.min(s.total || 1, (s.done || 0) + 1);
    const total = Math.max(done, s.total || 1);
    if (s.sent) {
      return t("cmSavingUploadedClip", "Saving {{done}} of {{total}}…", { done, total });
    }
    if (s.pct != null) {
      return t("cmUploadingClipsPercent", "Uploading {{done}} of {{total}} · {{pct}}%", {
        done,
        total,
        pct: s.pct,
      });
    }
    return t("cmUploadingClipsProgress", "Uploading {{done}} of {{total}}…", { done, total });
  }
  if (s.busy) return t("cmUploadingClips", "Uploading…");
  if (s.count > 0 && s.waitingReason) {
    switch (s.waitingReason) {
      case "offline":
        return t("cmClipsWaitOffline", "No network. Videos stay on this phone.");
      case "cellular":
        return t("cmClipsWaitCellular", "On mobile data. Videos upload on Wi‑Fi.");
      case "recording":
        return t("cmClipsWaitRecording", "Uploads start when this phone stops filming.");
      default:
        return t("cmClipsWaitTap", "Tap Upload now when this phone is on Wi‑Fi.");
    }
  }
  const r = s.lastResult;
  if (!r) return "";
  if (r.uploaded > 0 && r.failed === 0) {
    return t("cmClipsUploaded", "Uploaded {{n}} clips from this phone.", { n: r.uploaded });
  }
  if (r.uploaded > 0) {
    return t("cmClipsUploadedPartial", "Uploaded {{n}} clips. The rest stayed on this phone.", {
      n: r.uploaded,
    });
  }
  if (r.failed > 0) return clipUploadErrorText(r.error, t);
  if (r.forced && !s.count) {
    return t("cmNoClipsToUpload", "No clips are waiting on this phone.");
  }
  return "";
}

let countLoaded = null;

export async function refreshChampionshipClipQueue() {
  try {
    const summary = await summarizeLocalMatchClips();
    emit({ loaded: true, count: summary.count, bytes: summary.bytes });
    return summary;
  } catch {
    emit({ loaded: true, count: 0, bytes: 0 });
    return { count: 0, bytes: 0 };
  }
}

function ensureCountLoaded() {
  if (!countLoaded) {
    countLoaded = refreshChampionshipClipQueue().catch(() => null);
  }
  return countLoaded;
}

function setClipError(id, err) {
  const errors = { ...state.errors };
  if (err) errors[id] = classifyClipUploadError(err);
  else delete errors[id];
  emit({ errors });
}

/**
 * POST one clip with a stall guard: the request is aborted when no bytes have
 * moved for CLIP_UPLOAD_STALL_MS, and left alone once the body is sent (the
 * server may still be stitching).
 */
async function sendClip(
  { matchId, blob, fileName, fields, audioBlob },
  { onProgress, controllerRef } = {}
) {
  const controller = new AbortController();
  if (controllerRef) controllerRef.current = controller;
  let stallTimer = 0;
  let bodySent = false;
  const armStall = () => {
    window.clearTimeout(stallTimer);
    stallTimer = window.setTimeout(() => controller.abort(), CLIP_UPLOAD_STALL_MS);
  };
  armStall();
  let data;
  try {
    data = await uploadChampionshipMedia(matchId, blob, fields || {}, {
      filename: fileName || "clip.mp4",
      signal: controller.signal,
      onUploadProgress: (event) => {
        if (!event?.loaded) return;
        const sent = event.total > 0 && event.loaded >= event.total;
        if (sent) {
          bodySent = true;
          window.clearTimeout(stallTimer);
          stallTimer = 0;
        } else {
          armStall();
        }
        onProgress?.({
          sent,
          pct: event.total
            ? Math.min(99, Math.round((event.loaded / event.total) * 100))
            : null,
        });
      },
    });
  } catch (err) {
    if (err && typeof err === "object") err.bodySent = bodySent;
    throw err;
  } finally {
    window.clearTimeout(stallTimer);
    if (controllerRef?.current === controller) controllerRef.current = null;
  }
  if (audioBlob && audioBlob.size >= 256 && data?.media?.media_id) {
    try {
      await uploadChampionshipMediaAudio(matchId, data.media.media_id, audioBlob);
    } catch (audioErr) {
      console.warn("Match audio mix failed:", audioErr?.message || audioErr);
    }
  }
  return data;
}

const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

function hasAuthToken() {
  try {
    return Boolean(localStorage.getItem("token"));
  } catch {
    return false;
  }
}

/** Signed out or expired login: worth retrying once the user signs in again. */
function isAuthError(err) {
  return Number(err?.response?.status || err?.status) === 401;
}

async function keepOnPhone(clip) {
  const row = await saveLocalMatchClip(clip);
  emit({
    loaded: true,
    count: state.count + 1,
    bytes: state.bytes + (Number(clip.blob?.size) || 0),
  });
  return row;
}

/**
 * Park a clip on the phone first, then let the line send it. Used when the
 * camera screen closes mid-recording and the page may not live long enough
 * for a direct upload.
 */
export async function keepMatchClipOnPhone(clip) {
  const row = await keepOnPhone({
    ...clip,
    fields: {
      ...(clip.fields || {}),
      client_upload_id: clip.fields?.client_upload_id || newClientUploadId(),
    },
  });
  kickChampionshipClipUploads();
  return row;
}

/**
 * Send a just-recorded clip, or keep it on the phone. Footage is never
 * dropped: a clip the server refuses stays on the phone with its error so
 * the gallery can retry or delete it. Only a full phone throws.
 * @returns {Promise<{ held: true, reason: string, error?: object } | { data: any }>}
 */
export async function uploadOrKeepMatchClip({
  matchId,
  blob,
  fileName,
  mime,
  fields = {},
  audioBlob = null,
  thumb = null,
  info = null,
  recording = false,
}) {
  const clip = {
    matchId,
    blob,
    fileName,
    mime,
    fields: { ...fields, client_upload_id: fields.client_upload_id || newClientUploadId() },
    audioBlob,
    thumb,
    info,
  };
  await ensureCountLoaded();
  const plan = matchClipUploadPlan({
    mode: uploadModeForMatchClip(clip.fields, readMatchUploadMode()),
    transport: getNetworkTransport(),
    recording,
  });
  if (!plan.upload || state.count > 0) {
    await keepOnPhone(clip);
    if (plan.upload) kickChampionshipClipUploads();
    return { held: true, reason: plan.upload ? "queued" : plan.reason };
  }
  let lastErr = null;
  for (let attempt = 0; attempt <= LIVE_CLIP_RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      const data = await sendClip(clip);
      announceUploaded(matchId, data);
      return { data };
    } catch (err) {
      lastErr = err;
      if (!matchClipShouldStayOnPhone(err) || err?.bodySent) break;
      if (attempt < LIVE_CLIP_RETRY_DELAYS_MS.length) {
        await sleep(LIVE_CLIP_RETRY_DELAYS_MS[attempt]);
      }
    }
  }
  const row = await keepOnPhone(clip);
  if (!matchClipShouldStayOnPhone(lastErr) && !isAuthError(lastErr)) {
    rejectedIds.add(row.id);
    setClipError(row.id, lastErr);
    return { held: true, reason: "rejected", error: classifyClipUploadError(lastErr) };
  }
  kickChampionshipClipUploads();
  return { held: true, reason: "failed" };
}

const uploadController = { current: null };
let flushRun = null;
let forceRequested = false;
let cancelRequested = false;
/** Refused by the server this session; auto passes skip them until a tap. */
const rejectedIds = new Set();

/**
 * Upload waiting clips oldest first. Stops at the first network or server
 * failure (the rest keeps its order). `ids` limits the pass to some clips.
 */
export function flushChampionshipClips({ force = false, ids = null } = {}) {
  // A tap upgrades the pass in flight; the pass queued behind it covers a
  // pass that had already stopped for the network before the tap.
  if (force) forceRequested = true;
  if (flushRun && !ids && !force) return flushRun;
  const previous = flushRun || Promise.resolve();
  const run = previous
    .catch(() => null)
    .then(() => runFlushPass({ force: force || forceRequested, ids }));
  const tracked = run.finally(() => {
    if (flushRun === tracked) {
      flushRun = null;
      forceRequested = false;
    }
  });
  flushRun = tracked;
  return tracked;
}

async function runFlushPass({ force, ids }) {
  cancelRequested = false;
  const only = ids ? new Set(ids.map(String)) : null;
  const wantsForce = () => force || forceRequested;
  const tried = new Set();
  let uploaded = 0;
  let failed = 0;
  let firstError = null;
  let stopped = false;
  await ensureCountLoaded();
  emit({ busy: true, forced: wantsForce(), done: 0, total: 0, pct: null, waitingReason: "" });
  try {
    for (let pass = 0; pass < 4 && !stopped; pass += 1) {
      let rows;
      try {
        rows = await listLocalMatchClipMeta();
      } catch (err) {
        firstError = firstError || { kind: "unreadable", message: err?.message };
        break;
      }
      const todo = rows.filter(
        (row) =>
          !tried.has(row.id) &&
          (!only || only.has(row.id)) &&
          (wantsForce() || only || !rejectedIds.has(row.id))
      );
      if (!todo.length) break;
      emit({ total: uploaded + failed + todo.length });
      for (const meta of todo) {
        if (cancelRequested) {
          stopped = true;
          break;
        }
        const plan = matchClipUploadPlan({
          mode: uploadModeForMatchClip(meta.fields, readMatchUploadMode()),
          transport: getNetworkTransport(),
          recording: recordingActive,
          force: wantsForce() || Boolean(only),
        });
        if (!plan.upload) {
          emit({ waitingReason: plan.reason });
          stopped = true;
          break;
        }
        tried.add(meta.id);
        emit({ currentId: meta.id, pct: 0, sent: false, forced: wantsForce() });
        let row = null;
        try {
          row = await readLocalMatchClip(meta.id);
        } catch {
          row = null;
        }
        if (!row) continue;
        if (!row.matchId || !row.blob || row.blob.size < 256) {
          failed += 1;
          const err = { kind: "unreadable" };
          firstError = firstError || err;
          setClipError(meta.id, err);
          continue;
        }
        try {
          const data = await sendClip(
            {
              matchId: row.matchId,
              blob: row.blob,
              fileName: row.fileName,
              fields: row.fields,
              audioBlob: row.audioBlob,
            },
            {
              controllerRef: uploadController,
              onProgress: ({ sent, pct }) => emit({ sent, pct }),
            }
          );
          await deleteLocalMatchClip(row.id);
          rejectedIds.delete(row.id);
          setClipError(row.id, null);
          uploaded += 1;
          emit({
            done: uploaded,
            count: Math.max(0, state.count - 1),
            bytes: Math.max(0, state.bytes - (Number(meta.byteSize) || row.blob.size || 0)),
          });
          announceUploaded(row.matchId, data);
        } catch (err) {
          if (cancelRequested) {
            stopped = true;
            break;
          }
          failed += 1;
          firstError = firstError || err;
          setClipError(row.id, err);
          if (matchClipShouldStayOnPhone(err) || isAuthError(err)) {
            stopped = true;
            break;
          }
          rejectedIds.add(row.id);
        }
      }
    }
  } finally {
    await refreshChampionshipClipQueue();
    const result = {
      uploaded,
      failed,
      error: firstError ? classifyClipUploadError(firstError) : null,
      forced: wantsForce(),
      at: Date.now(),
    };
    emit({
      busy: false,
      forced: false,
      currentId: null,
      pct: null,
      sent: false,
      lastResult: uploaded || failed || wantsForce() ? result : state.lastResult,
    });
  }
  return { uploaded, failed };
}

/** Stop the upload in flight; the clip stays on the phone. */
export function cancelChampionshipClipUpload() {
  cancelRequested = true;
  uploadController.current?.abort();
}

export async function deleteChampionshipClips(ids) {
  const list = (ids || []).map(String).filter(Boolean);
  if (list.includes(String(state.currentId || ""))) {
    cancelChampionshipClipUpload();
    await (flushRun || Promise.resolve()).catch(() => null);
  }
  for (const id of list) {
    await deleteLocalMatchClip(id).catch(() => null);
    rejectedIds.delete(id);
    setClipError(id, null);
  }
  return refreshChampionshipClipQueue();
}

let kickTimer = 0;

/** Drain the line soon (coalesces bursts of network events). */
export function kickChampionshipClipUploads() {
  if (typeof window === "undefined") return;
  window.clearTimeout(kickTimer);
  kickTimer = window.setTimeout(() => {
    kickTimer = 0;
    if (flushRun || !hasAuthToken()) return;
    void ensureCountLoaded().then(() => {
      if (state.count > 0) void flushChampionshipClips();
    });
  }, 400);
}

let autoUsers = 0;
let autoStop = null;

/**
 * Keep the line draining while the app is open: network changes, returning
 * to the foreground, and a periodic retry. Ref-counted; returns a stop fn.
 */
export function startChampionshipClipAutoUpload() {
  autoUsers += 1;
  if (!autoStop && typeof window !== "undefined") {
    const kick = () => kickChampionshipClipUploads();
    const onVisible = () => {
      if (document.visibilityState === "visible") kick();
    };
    const tick = () => {
      if (state.count > 0 && !flushRun) kick();
    };
    window.addEventListener("online", kick);
    document.addEventListener("visibilitychange", onVisible);
    const connection = navigator.connection;
    connection?.addEventListener?.("change", kick);
    const timer = window.setInterval(tick, CLIP_AUTO_FLUSH_MS);
    if (localMatchClipsMayExist()) kick();
    autoStop = () => {
      window.removeEventListener("online", kick);
      document.removeEventListener("visibilitychange", onVisible);
      connection?.removeEventListener?.("change", kick);
      window.clearInterval(timer);
    };
  }
  return () => {
    autoUsers -= 1;
    if (autoUsers <= 0 && autoStop) {
      autoStop();
      autoStop = null;
      autoUsers = 0;
    }
  };
}

export function resetChampionshipClipUploaderForTests() {
  state = IDLE;
  countLoaded = null;
  flushRun = null;
  forceRequested = false;
  cancelRequested = false;
  recordingActive = false;
  rejectedIds.clear();
  listeners.clear();
  uploadedListeners.clear();
}
